import React from "react";
import { Box } from "@chakra-ui/react";

export const MagicPortalBackground: React.FC = () => {
  return (
    <Box
      position="absolute"
      top="0"
      left="0"
      width="100%"
      height="100%"
      overflow="hidden"
      zIndex="0"
      pointerEvents="none"
    >
      {/* Deep purple base */}
      <Box
        position="absolute"
        width="100%"
        height="100%"
        background="radial-gradient(ellipse at center, #4a1a6b 0%, #2d0a45 40%, #1a0530 70%, #0d0218 100%)"
      />

      {/* Swirling portal vortex */}
      <Box
        className="portal-vortex"
        position="absolute"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        width="150vmax"
        height="150vmax"
      >
        {/* Outer swirl rings */}
        <Box
          className="swirl-ring-1"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="100%"
          height="100%"
          borderRadius="50%"
          border="3px solid"
          borderColor="rgba(255,100,200,0.15)"
          boxShadow="0 0 60px rgba(255,100,200,0.1), inset 0 0 60px rgba(255,100,200,0.05)"
        />
        <Box
          className="swirl-ring-2"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="85%"
          height="85%"
          borderRadius="50%"
          border="4px solid"
          borderColor="rgba(200,80,255,0.2)"
          boxShadow="0 0 50px rgba(200,80,255,0.15), inset 0 0 50px rgba(200,80,255,0.08)"
        />
        <Box
          className="swirl-ring-3"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="70%"
          height="70%"
          borderRadius="50%"
          border="5px solid"
          borderColor="rgba(255,120,220,0.25)"
          boxShadow="0 0 40px rgba(255,120,220,0.2), inset 0 0 40px rgba(255,120,220,0.1)"
        />
        <Box
          className="swirl-ring-4"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="55%"
          height="55%"
          borderRadius="50%"
          border="6px solid"
          borderColor="rgba(180,60,255,0.3)"
          boxShadow="0 0 35px rgba(180,60,255,0.25), inset 0 0 35px rgba(180,60,255,0.12)"
        />
        <Box
          className="swirl-ring-5"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="40%"
          height="40%"
          borderRadius="50%"
          border="8px solid"
          borderColor="rgba(255,150,230,0.35)"
          boxShadow="0 0 30px rgba(255,150,230,0.3), inset 0 0 30px rgba(255,150,230,0.15)"
        />

        {/* Inner glow */}
        <Box
          className="portal-center-glow"
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          width="25%"
          height="25%"
          borderRadius="50%"
          background="radial-gradient(ellipse at center, rgba(255,200,255,0.4) 0%, rgba(200,100,255,0.2) 50%, transparent 70%)"
        />
      </Box>

      {/* Swirling particles/energy streams */}
      <svg
        className="portal-streams-svg"
        viewBox="0 0 100 100"
        preserveAspectRatio="xMidYMid slice"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
        }}
      >
        <defs>
          <linearGradient id="streamGrad1" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#ff80d0" stopOpacity="0" />
            <stop offset="50%" stopColor="#ff80d0" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#ff80d0" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="streamGrad2" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#c060ff" stopOpacity="0" />
            <stop offset="50%" stopColor="#c060ff" stopOpacity="0.5" />
            <stop offset="100%" stopColor="#c060ff" stopOpacity="0" />
          </linearGradient>
        </defs>

        {/* Spiral energy streams */}
        <g className="energy-streams">
          <path className="stream-1" d="M50,50 Q70,30 80,50 T90,70" stroke="url(#streamGrad1)" strokeWidth="0.3" fill="none" />
          <path className="stream-2" d="M50,50 Q30,70 20,50 T10,30" stroke="url(#streamGrad2)" strokeWidth="0.3" fill="none" />
          <path className="stream-3" d="M50,50 Q60,20 50,10 T30,20" stroke="url(#streamGrad1)" strokeWidth="0.25" fill="none" />
          <path className="stream-4" d="M50,50 Q40,80 50,90 T70,80" stroke="url(#streamGrad2)" strokeWidth="0.25" fill="none" />
        </g>
      </svg>

      {/* Falling/swirling magic elements container */}
      <Box className="magic-elements" position="absolute" width="100%" height="100%" top="0" left="0">
        {/* Stars */}
        {[...Array(12)].map((_, i) => (
          <Box
            key={`star-${i}`}
            className={`falling-star falling-star-${i + 1}`}
            position="absolute"
            opacity="0"
          >
            <svg width="24" height="24" viewBox="0 0 24 24">
              <polygon
                points="12,2 15,9 22,9 16,14 18,22 12,17 6,22 8,14 2,9 9,9"
                fill={i % 3 === 0 ? "#ffd700" : i % 3 === 1 ? "#ffb6c1" : "#e6e6fa"}
                filter="drop-shadow(0 0 4px currentColor)"
              />
            </svg>
          </Box>
        ))}

        {/* Wands */}
        {[...Array(8)].map((_, i) => (
          <Box
            key={`wand-${i}`}
            className={`falling-wand falling-wand-${i + 1}`}
            position="absolute"
            opacity="0"
          >
            <svg width="40" height="40" viewBox="0 0 40 40">
              {/* Wand stick */}
              <rect x="18" y="12" width="4" height="24" rx="1" fill={i % 2 === 0 ? "#8b4513" : "#654321"} />
              {/* Wand tip glow */}
              <circle cx="20" cy="10" r="6" fill="url(#wandTipGradient)" />
              <circle cx="20" cy="10" r="3" fill="#ffffff" />
              {/* Sparkles around tip */}
              <circle cx="14" cy="8" r="1.5" fill="#ffd700" className="wand-sparkle" />
              <circle cx="26" cy="8" r="1.5" fill="#ff69b4" className="wand-sparkle" />
              <circle cx="20" cy="4" r="1.5" fill="#00ffff" className="wand-sparkle" />
              <defs>
                <radialGradient id="wandTipGradient">
                  <stop offset="0%" stopColor="#ffffff" />
                  <stop offset="40%" stopColor="#ffd700" />
                  <stop offset="100%" stopColor="#ff69b4" stopOpacity="0" />
                </radialGradient>
              </defs>
            </svg>
          </Box>
        ))}

        {/* Small sparkle particles */}
        {[...Array(20)].map((_, i) => (
          <Box
            key={`sparkle-${i}`}
            className={`portal-sparkle portal-sparkle-${i + 1}`}
            position="absolute"
            width={3 + (i % 3) * 2 + "px"}
            height={3 + (i % 3) * 2 + "px"}
            borderRadius="50%"
            backgroundColor={
              i % 4 === 0 ? "#ffd700" :
              i % 4 === 1 ? "#ff69b4" :
              i % 4 === 2 ? "#c060ff" :
              "#ffffff"
            }
            boxShadow={`0 0 ${4 + (i % 3) * 2}px currentColor`}
            opacity="0"
          />
        ))}
      </Box>

      {/* Ambient magical mist */}
      <Box
        className="portal-mist-1"
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        background="radial-gradient(ellipse at 30% 70%, rgba(255,100,200,0.1) 0%, transparent 50%)"
      />
      <Box
        className="portal-mist-2"
        position="absolute"
        top="0"
        left="0"
        width="100%"
        height="100%"
        background="radial-gradient(ellipse at 70% 30%, rgba(150,50,255,0.1) 0%, transparent 50%)"
      />
    </Box>
  );
};

export default MagicPortalBackground;
